import { DateTime } from 'luxon'
import { seDateTimeConfig } from './date-time-config'
import {
  SeDateTimeChunk,
  SeDateTimeChunkFinder,
  SeDateTimeFormatCondition,
  SeDateTimeFormatModifier,
  SeDateTimeModifier,
  SeDateTimeFormat,
  SeDateTimeOptions
} from './date-time-types'

const TOKEN: RegExp = /(\{[^\}]+\})/g
const NON_TIME_TOKENS: string[] = [
  SeDateTimeFormat.TimeTbd,
  SeDateTimeFormat.AllDay,
  SeDateTimeFormat.At,
  SeDateTimeFormat.From,
  SeDateTimeFormat.To
]
const NON_TIME_TOKEN: RegExp = new RegExp(`\\{(${NON_TIME_TOKENS.join('|')})\\}`, 'g')

export class SeDateTimeFormatter {

  protected conditions: SeDateTimeFormatCondition[]
  protected isValid: boolean
  protected isValidRange: boolean
  protected allDay: boolean
  protected timeTbd: boolean
  protected hasTime: boolean
  protected cache: { [key: string]: any }
  protected findChunks: SeDateTimeChunkFinder
  protected options: SeDateTimeOptions = {}
  protected currentYear: number

  protected addConditions(conditions: SeDateTimeFormatCondition[]): void {
    this.conditions = (this.conditions || []).concat(conditions)
  }

  protected cached<T>(name: string, cacheFn: () => T): T {
    const cache = this.cache = this.cache || {}
    if (name in cache) return cache[name]
    return cache[name] = cacheFn()
  }

  protected format(format: string, rangeCheck?: () => boolean): string {
    this.currentYear = this.currentYear || (new Date()).getFullYear()
    return this.cached(format, () => {
      if (!this.isValid) return
      format = this.modifiedFormat(format, rangeCheck)
      let value = this.getFormat(format)
      if (!this.nonTimeToken(format)) {
        value = this
          .findChunks(value)
          .map(chunk => this.formatChunk(chunk))
          .join('')
      }
      return value ? `${value[0].toUpperCase()}${value.substring(1)}` : undefined
    })
  }

  private escapeRegExp(str: string): string {
    return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
  }

  private formatChunk(chunk: SeDateTimeChunk, nested: boolean = false): string {
    let format = chunk.format
    const luxon = chunk.luxon

    // first, replace conditional - conditions look like { 'token': () => boolean }
    this.conditions.forEach(condition => {
      for (const token in condition) {
        const enabled = condition[token](luxon)
        const matcher = new RegExp(`\\{([^{]+)${this.escapeRegExp(token)}\\}`, 'g')
        format = this.replaceToken(format, matcher, luxon, enabled)
      }
    })

    // then, replace non-time tokens, escaped so they don't get luxon formatted later
    format = format.replace(NON_TIME_TOKEN, match => `'${this.getFormat(match.substring(1, match.length - 1))}'`)

    // finally, replace all other format tokens
    format = this.replaceToken(format, TOKEN, luxon, true)

    if (!nested && format) format = luxon.toFormat(format)
    return format || ''
  }

  protected modifiedFormat(format: string, rangeCheck?: () => boolean): string {
    const showAsRange = this.isValidRange && (rangeCheck ? rangeCheck() : false)
    // Range
    if (showAsRange) format = this.includeSuffix(format, SeDateTimeModifier.Range)
    // Time
    if (this.allDay) format = this.includeSuffix(format, SeDateTimeModifier.AllDay)
    else if (this.timeTbd) format = this.includeSuffix(format, SeDateTimeModifier.TimeTbd)
    else if (format === SeDateTimeFormat.Time) return SeDateTimeFormat.TimeWithZone // special case
    // Same Day
    if (showAsRange && !this.differentDays) format = this.includeSuffix(format, SeDateTimeModifier.SameDay)

    return format
  }

  // Returns the format string (tokens) for the given key if defined
  protected getFormat(format: string): string {
    return seDateTimeConfig.formats[format] || format
  }

  private replaceToken(str: string, matcher: RegExp, luxon: DateTime, enabled: boolean): string {
    return str.replace(matcher, (match: string) => {
      const formatName = match.split(/<|\}/)[0].substring(1)
      const format = enabled ? this.getFormat(formatName) : ''
      if (this.nonTimeToken(formatName)) return `'${format}'`
      return TOKEN.test(format) ? this.formatChunk({ format, luxon }, true) : format
    })
  }

  private nonTimeToken(format: string): boolean {
    return NON_TIME_TOKENS.indexOf(format) !== -1
  }

  protected includeSuffix(format: string, suffix: string): string {
    const suffixedFormat = `${format}-${suffix}`
    return this.getFormat(suffixedFormat) ? suffixedFormat : format
  }

  protected get differentYears(): boolean {
    const range = this as any // hack to prevent type errors
    return this.isValidRange && range.start.luxon.year !== range.end.luxon.year && this.differentDays
  }

  protected get differentMonths(): boolean {
    const range = this as any // hack to prevent type errors
    return this.isValidRange && (this.differentYears || range.start.luxon.month !== range.end.luxon.month) && this.differentDays
  }

  // NOTE: This is special logic to treat time ranges that start late on one day and
  // run through the early morning hours of the following day as the same day.
  protected get differentDays(): boolean {
    return this.cached('differentDays', () => {
      if (!this.isValidRange) return false
      const range = this as any // hack to prevent type errors
      const s = range.start.luxon
      const e = range.end.luxon
      const startSeconds = s.startOf('day').toSeconds()
      const endSeconds = e.startOf('day').toSeconds()
      const nextDaySeconds = s.startOf('day').plus({ days: 1 }).toSeconds()
      const sameDay = startSeconds === endSeconds
      const nextDay = this.hasTime && nextDaySeconds === endSeconds
      const lateNightRange = nextDay && s.hour > 11 && e.hour < 5
      return !sameDay && !lateNightRange
    })
  }

  protected get nonCurrentYear(): boolean {
    if (this.startLuxon.year !== this.currentYear) return true
    if (this.endLuxon && this.endLuxon.isValid && this.endLuxon.year !== this.currentYear) return true
  }

  protected get startLuxon(): DateTime {
    const self = this as any
    return self.start ? self.start.luxon : self.luxon
  }

  protected get endLuxon(): DateTime {
    const self = this as any
    return self.end ? self.end.luxon : undefined
  }
}
